/**
 * LOT-BASIERTES PREISSYSTEM
 * 
 * Ersetzt das problematische Durchschnittspreis-System durch ein präzises
 * Lot-basiertes Preissystem mit FIFO/LIFO/FEFO Unterstützung.
 */

import {
  getCurrentBusinessId,
  businessDataStorageKey,
  readBestBusinessMergedPayload,
  mergeBusinessPayloadPreserveRicher,
  normalizeBusinessStorageId
} from './sharedCore';
import { readLotsSyncForBusiness, seedLotsReadCache } from './lotsReadSource';
import { readPurchasesFromMergedBlob } from './purchasesReadSource';
import { normalizePurchasesField, normalizeLotsField } from './businessStateSync';
import {
  queueBusinessPayloadWrite,
  queueBusinessPayloadFieldWrite,
  queueEntityItemUpsert
} from '../infrastructure/storage/businessPayloadWrite';
import { isEntityStoreNoKvBlobEnabled } from '../infrastructure/storage/businessPayloadMeta';
import { isEntityStoreEnabled } from '../infrastructure/storage/entityAggregateStore';
import { getBusinessPayloadRepository } from '../app/di/container';
import { publishLotUpdated, publishLegacyWindowEvent, publishForceStockRefresh } from '../infrastructure/events/legacyEventBridge';
import { sameProductId, sameLotId } from './productIdUtils';
import { readStoredSessionUser } from '../services/offlineAuthSession';
import { kvGetRaw, kvRemove, kvKeysStartingWith } from '../infrastructure/storage/kvAccess';
import {
  appendStockMovements,
  buildAdjustmentMovement,
  INVENTORY_LEDGER_AUTHORITATIVE_FIELDS
} from '../domain/inventory/movements';
import {
  assertLotMergeQuantityAllowed,
  assertLotQuantityMutationAllowed,
  assignParsedLotsGuarded
} from '../domain/inventory/guards/inventoryLotMutationGuard';
import { applyPlannedLotsViaLedger } from '../domain/inventory/lotPlanViaLedger';

function emitStockUpdated(detail = {}) {
  publishLegacyWindowEvent('stockUpdated', detail);
}

function emitLotUpdated(detail = {}) {
  publishLotUpdated(detail);
}

/** Phase 5i — kanonischer Lesepfad für vollständigen Business-Blob. */
function loadBusinessPayloadBest(businessId) {
  return readBestBusinessMergedPayload(businessId).parsed ?? null;
}

/**
 * Blob/shell alone often has empty lots under entity-store / NO_KV_BLOB.
 * Sync read cache (and React inventory) is the live SSOT for lot ids.
 */
function resolveLotsForSyncMutator(businessId, parsedLots) {
  const fromParsed = Array.isArray(parsedLots)
    ? parsedLots
    : parsedLots && typeof parsedLots === 'object'
      ? Object.values(parsedLots)
      : [];
  let fromSync = [];
  try {
    fromSync = readLotsSyncForBusiness(businessId) || [];
  } catch {
    fromSync = [];
  }
  if (!Array.isArray(fromSync) || fromSync.length === 0) return fromParsed;
  if (fromParsed.length === 0) return fromSync;

  // Union by id — prefer sync copy when both exist (fresher remainingQuantity)
  const byId = new Map();
  for (const lot of fromParsed) {
    if (lot?.id != null) byId.set(String(lot.id), lot);
  }
  for (const lot of fromSync) {
    if (lot?.id != null) byId.set(String(lot.id), lot);
  }
  return [...byId.values()];
}

function cloneLotsForDryRun(lots) {
  return (Array.isArray(lots) ? lots : []).map((lot) =>
    lot && typeof lot === 'object' ? { ...lot } : lot
  );
}

/**
 * Sync-Persistenz für Legacy-Aufrufer: berechnet merged Payload lokal
 * (damit Mutator-Side-Effects wie resultLot sofort greifen), schreibt
 * serialisiert über queueBusinessPayloadWrite (kein direktes KV-Write).
 */
function persistBusinessPayloadSync(businessId, mutator, authoritativeFields = ['lots']) {
  const id = normalizeBusinessStorageId(
    businessId || getCurrentBusinessId({ allowFallback: false })
  );
  if (!id) return { success: false, error: 'No business ID found' };

  const current = loadBusinessPayloadBest(id) || {};
  const needsLotsHydration =
    !authoritativeFields?.length || authoritativeFields.includes('lots');
  const lotsSource = needsLotsHydration
    ? resolveLotsForSyncMutator(id, current.lots)
    : Array.isArray(current.lots)
      ? current.lots
      : current.lots && typeof current.lots === 'object'
        ? Object.values(current.lots)
        : [];

  // Shallow-clone lots so dry-run mutations never touch the cached payload
  const dryBase = {
    ...current,
    lots: cloneLotsForDryRun(lotsSource)
  };

  let syncUpdated;
  try {
    syncUpdated = mutator(dryBase);
    if (!syncUpdated || typeof syncUpdated !== 'object') {
      return { success: false, error: 'Mutator returned invalid payload' };
    }
    ensureLotsArray(syncUpdated);
  } catch (err) {
    return { success: false, error: err?.message || String(err) };
  }

  // Seed sync read-cache immediately so the next edit sees this dry-run (no race on stale lots).
  try {
    if (Array.isArray(syncUpdated.lots)) {
      seedLotsReadCache(id, syncUpdated.lots);
    }
  } catch {
    /* best-effort */
  }

  const writePromise = queueBusinessPayloadWrite(
    id,
    (parsed) => {
      const base = { ...parsed };
      if (needsLotsHydration) {
        // Queue path usually has entity overlay; still hydrate if lots missing
        const overlayLots = resolveLotsForSyncMutator(id, base.lots);
        if (!base.lots?.length || overlayLots.length > (base.lots?.length || 0)) {
          base.lots = cloneLotsForDryRun(overlayLots);
        }
      }
      const updated = mutator(base);
      ensureLotsArray(updated);
      return updated;
    },
    { authoritativeFields }
  ).catch((err) => {
    console.warn('[lotSystem] queueBusinessPayloadWrite failed:', err?.message || err);
    return null;
  });

  return { success: true, parsed: syncUpdated, writePromise };
}

function parseKvCurrentUser() {
  return readStoredSessionUser();
}

function businessIdFromUser(user) {
  if (!user) return null;
  let raw = null;
  if (user.type === 'business' && user.business?.id) raw = user.business.id;
  else if (user.business?.auth_user_id) raw = user.business.auth_user_id;
  else if (user.user?.id) raw = user.user.id;
  else if (user.id) raw = user.id;
  return raw != null ? normalizeBusinessStorageId(raw) : null;
}

// Lot-Status Konstanten
export const LOT_STATUS = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  SOLD_OUT: 'sold_out',
  TRANSFERRED: 'transferred'
};

// Preismethoden Konstanten
export const PRICING_METHODS = {
  FIFO: 'fifo',  // First In, First Out
  LIFO: 'lifo',  // Last In, First Out
  FEFO: 'fefo',  // First Expired, First Out
  AVERAGE: 'average' // Fallback für Kompatibilität
};

/** Local calendar day start as epoch ms (one allocation for a whole aggregation pass). */
export function startOfLocalDayMs(referenceDate = new Date()) {
  if (typeof referenceDate === 'number' && Number.isFinite(referenceDate)) {
    return referenceDate;
  }
  const d = referenceDate instanceof Date ? new Date(referenceDate.getTime()) : new Date(referenceDate);
  if (Number.isNaN(d.getTime())) {
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback.getTime();
  }
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Expiration as local calendar-day epoch ms — cheap for YYYY-MM-DD strings.
 * @param {string|Date|null|undefined} expirationDate
 * @returns {number|null}
 */
export function expirationDayMs(expirationDate) {
  if (expirationDate == null || expirationDate === '') return null;
  if (typeof expirationDate === 'string') {
    const raw = expirationDate.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      const y = Number(raw.slice(0, 4));
      const m = Number(raw.slice(5, 7)) - 1;
      const day = Number(raw.slice(8, 10));
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) return null;
      return new Date(y, m, day).getTime();
    }
  }
  const exp = new Date(expirationDate);
  if (Number.isNaN(exp.getTime())) return null;
  exp.setHours(0, 0, 0, 0);
  return exp.getTime();
}

/**
 * Kalendertag-Vergleich: abgelaufen wenn Ablaufdatum vor heute (lokal).
 * @param {object} lot
 * @param {Date|number} [referenceDate] — Date or startOfLocalDayMs number
 */
export function isLotExpired(lot, referenceDate = new Date()) {
  const expMs = expirationDayMs(lot?.expirationDate);
  if (expMs == null) return false;
  const todayMs = startOfLocalDayMs(referenceDate);
  return expMs < todayMs;
}

/**
 * Lot für Verkauf/FIFO/Bestand nutzbar — gleiche Regel wie Stock/Inventaire/Bilan
 * (isLotIncludedInStockMetrics): aktiv, Menge > 0, nicht kalendarisch abgelaufen.
 * Fehlender status = legacy active.
 */
export function isLotAvailableForSale(lot, referenceDate = new Date()) {
  if (!lot || typeof lot !== 'object') return false;
  if (Number(lot.remainingQuantity) <= 0) return false;
  if (lot.status === LOT_STATUS.EXPIRED) return false;
  if (lot.status === LOT_STATUS.SOLD_OUT) return false;
  if (lot.status === LOT_STATUS.TRANSFERRED) return false;
  if (lot.status && lot.status !== LOT_STATUS.ACTIVE) return false;
  if (isLotExpired(lot, referenceDate)) return false;
  return true;
}

let lotIdSequence = 0;

/**
 * Generiert eine eindeutige Lot-ID
 * Format: LOT_YYYY_<ms>_<seq> (seq verhindert Kollisionen bei Multi-Produkt-Achats)
 */
export const generateLotId = () => {
  lotIdSequence += 1;
  const year = new Date().getFullYear();
  return `LOT_${year}_${Date.now()}_${lotIdSequence}`;
};

let batchNumberSequence = 0;

/**
 * Generiert die menschenlesbare Lot-Nummer (Feld `batchNumber` / UI „N° lot“).
 * Nicht verwechseln mit `lot.id` (interne System-ID: LOT_YYYY_…).
 * Format: BYYMMDD_<ms>_<seq>
 */
export const generateBatchNumber = (date = new Date()) => {
  batchNumberSequence += 1;
  const d = new Date(date);
  const year = d.getFullYear().toString().slice(-2);
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `B${year}${month}${day}_${Date.now()}_${batchNumberSequence}`;
};

/** Transfer-Suffix `_T{timestamp}` von der Lot-Nr. entfernen (Anzeige / Vergleich). */
export function stripTransferLotNumberSuffix(batchNumber) {
  return String(batchNumber ?? '')
    .trim()
    .replace(/_T\d{10,}$/i, '');
}

/**
 * Anzeige-Lot-Nummer für POS/UI: immer `lot.id` (LOT_…), nicht die Auto-Batch (B…).
 * Batch-Feld nur als Fallback, wenn keine Lot-ID existiert.
 */
export function resolveLotNumber(lot) {
  if (lot?.id != null && lot.id !== '') return String(lot.id);
  return stripTransferLotNumberSuffix(lot?.batchNumber);
}

/**
 * Verkaufspreis für Wareneingang/Restock.
 * Leer oder ≤0 bedeutet „gleich Einkaufspreis“ — sonst entsteht bei jedem Achat
 * ein neues Lot (createLot speichert Fallback, findLot vergleicht 0).
 */
export function resolveReceiveSellingUnitPrice(source, purchaseUnitPrice = null) {
  const buy = Number(purchaseUnitPrice ?? source?.purchaseUnitPrice ?? 0);
  const raw = source?.sellingUnitPrice ?? source?.sellingPrice;
  if (raw == null || raw === '') {
    return Number.isFinite(buy) ? buy : 0;
  }
  const sell = Number(raw);
  if (!Number.isFinite(sell) || sell <= 0) {
    return Number.isFinite(buy) ? buy : 0;
  }
  return sell;
}

/** Geldvergleich auf Cent (vermeidet 10 vs 10.00 / Float-Drift). */
export function sameLotMoney(a, b) {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return false;
  return Math.round(na * 100) === Math.round(nb * 100);
}

/**
 * Lot darf für Restock/Achat-Mengen-Update genutzt werden
 * (inkl. sold_out / Menge 0 — sonst entsteht fälschlich ein neues Lot).
 */
export function isLotEligibleForReceiveRestock(lot, referenceDate = new Date()) {
  if (!lot || typeof lot !== 'object') return false;
  if (lot.status === LOT_STATUS.TRANSFERRED) return false;
  if (lot.status === LOT_STATUS.EXPIRED) return false;
  if (isLotExpired(lot, referenceDate)) return false;
  if (lot.status && lot.status !== LOT_STATUS.ACTIVE && lot.status !== LOT_STATUS.SOLD_OUT) {
    return false;
  }
  return true;
}

/**
 * Bestehendes Lot für Wareneingang (Achat): gleiche Location + EK + Ablauf
 * → Lot-Update statt neues Lot / neue Batch-Nummer.
 * Verkaufspreis wird beim Restock aktualisiert, blockiert das Matching aber nicht.
 */
export function findLotForReceiveRestock(line, contextLots = null) {
  if (!line || line.productId == null || line.productId === '') return null;
  const location = line.location || 'Principal';
  const toList = (raw) => (Array.isArray(raw) ? raw : Object.values(raw || {}));

  let pool = [];
  if (contextLots != null) {
    pool = filterLotsByProductAndLocation(toList(contextLots), line.productId, location);
  }
  // Empty context must not skip storage — otherwise every achat creates a new lot
  if (!pool.length) {
    pool = getLotsForProduct(line.productId, location, null);
  }
  pool = pool.filter((lot) => isLotEligibleForReceiveRestock(lot));
  if (!pool.length) return null;

  const lineNo = String(line.lotId || line.lotNumber || line.batchNumber || '').trim();
  if (lineNo) {
    const byNumber = pool.find(
      (lot) =>
        sameLotId(lot.id, lineNo) ||
        String(lot.id) === lineNo ||
        stripTransferLotNumberSuffix(lot.batchNumber) === stripTransferLotNumberSuffix(lineNo)
    );
    if (byNumber) return byNumber;
  }

  const buy = Number(line.purchaseUnitPrice);
  const exp = normalizeLotExpiryKey(line.expirationDate);

  return (
    pool.find((lot) => {
      const lotBuy = Number(lot.purchaseUnitPrice);
      return (
        sameLotMoney(lotBuy, buy) &&
        normalizeLotExpiryKey(lot.expirationDate) === exp
      );
    }) || null
  );
}

/**
 * Erstellt ein neues Lot-Objekt (In-Memory).
 * ADR-0013: Prefer domain/inventory/receive (Wareneingang) or domain/inventory/lots.createLot.
 * @deprecated Direct utils/lotSystem writes — import from domain/inventory/lots instead.
 */
export const createLot = (lotData) => {
  
  const {
    productId,
    purchaseId = null,
    purchaseUnitPrice,
    sellingUnitPrice,
    quantity,
    expirationDate,
    purchaseDate = new Date().toISOString(),
    supplier = '',
    location = 'Principal',
    batchNumber: batchNumberInput = null,
    lotNumber = null,
    id: explicitId = null
  } = lotData;

  const lotId =
    explicitId != null && String(explicitId).trim() !== ''
      ? String(explicitId).trim()
      : generateLotId();
  // Lot-Nummer = lot.id. Keine separate Auto-Batch (B…) bei Achat — nur wenn explizit übergeben.
  const batchNumber =
    (batchNumberInput != null && String(batchNumberInput).trim() !== ''
      ? String(batchNumberInput).trim()
      : null) ||
    (lotNumber != null && String(lotNumber).trim() !== '' ? String(lotNumber).trim() : null) ||
    lotId;

  // Keep string productIds (float-like catalog ids) — Number() loses precision / breaks links
  const resolvedProductId =
    productId == null || productId === ''
      ? productId
      : typeof productId === 'number'
        ? productId
        : Number.isFinite(Number(productId)) && String(Number(productId)) === String(productId)
          ? Number(productId)
          : productId;

  const qty = Number(quantity) || 0;
  const expiredAtCreate = Boolean(expirationDate) && isLotExpired({ expirationDate });
  const remaining = expiredAtCreate ? 0 : qty;
  const unitPurchase = Number(purchaseUnitPrice);
  const unitSell = resolveReceiveSellingUnitPrice(
    { sellingUnitPrice, purchaseUnitPrice: unitPurchase },
    unitPurchase
  );
  const lot = {
    id: lotId,
    productId: resolvedProductId,
    purchaseId: purchaseId ? String(purchaseId) : null,
    batchNumber,
    expirationDate: expirationDate || null,
    purchaseDate,
    supplier,
    location,
    
    // Preise
    purchaseUnitPrice: unitPurchase,
    sellingUnitPrice: unitSell,
    
    // Mengen
    totalQuantity: qty,
    remainingQuantity: remaining,
    ...(expiredAtCreate
      ? {
          expiredQuantity: qty,
          expiredAt: new Date().toISOString()
        }
      : {}),
    
    // Werte
    totalPurchaseValue: remaining * unitPurchase,
    totalSellingValue: remaining * unitSell,
    potentialProfit: remaining * (unitSell - unitPurchase),
    
    // Status — past-dated lots never enter sellable stock
    status: expiredAtCreate ? LOT_STATUS.EXPIRED : LOT_STATUS.ACTIVE,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  
  return lot;
};

function filterLotsByProductAndLocation(lots, productId, location = null) {
  const list = Array.isArray(lots) ? lots : [];
  if (productId === null) return list;

  const locKey = location ? String(location).trim().toLowerCase() : '';
  return list.filter((lot) => {
    if (!lot || typeof lot !== 'object') return false;
    const matchesProduct = sameProductId(lot.productId, productId);
    const lotLoc = String(lot.location || 'Principal').trim().toLowerCase();
    const matchesLocation = !locKey || lotLoc === locKey;
    return matchesProduct && matchesLocation;
  });
}

/**
 * Lots eines Einkaufsbelegs (purchaseId / ACH-…).
 */
export function getLotsForPurchase(purchaseId, contextLots = null) {
  if (purchaseId == null || purchaseId === '') return [];
  const id = String(purchaseId);
  try {
    const toList = (raw) => (Array.isArray(raw) ? raw : Object.values(raw || {}));
    const fromContext = contextLots != null ? toList(contextLots) : [];
    const fromContextForReceipt = fromContext.filter(
      (lot) => String(lot?.purchaseId ?? '') === id
    );
    // Live React lots win when they already know this receipt (edit modal)
    if (fromContextForReceipt.length > 0) {
      return fromContextForReceipt;
    }

    const businessId = getCurrentBusinessId();
    const list =
      businessId && businessDataStorageKey(businessId)
        ? readLotsSyncForBusiness(businessId)
        : fromContext;
    return (list || []).filter((lot) => String(lot?.purchaseId ?? '') === id);
  } catch {
    return [];
  }
}

/**
 * Lots eines Produkts — bei mitgegebenem DataContext (Stock Zentral) hat die
 * Live-Liste Vorrang vor dem Sync-Storage-Cache (sonst falsche remainingQuantity).
 * @param {number|string|null} productId
 * @param {string|null} location
 * @param {object[]|object|null} [contextLots] — lots aus useInventoryData / Stock Zentral
 */
export const getLotsForProduct = (productId, location = null, contextLots = null) => {
  try {
    const toList = (raw) => (Array.isArray(raw) ? raw : Object.values(raw || {}));

    const fromContext =
      contextLots != null
        ? filterLotsByProductAndLocation(toList(contextLots), productId, location)
        : [];

    // Stock Zentral / POS pass live lots — use them when they contain this product.
    if (fromContext.length > 0) {
      return dedupeLotsList(fromContext);
    }

    const businessId = getCurrentBusinessId();
    if (!businessId || !businessDataStorageKey(businessId)) {
      return dedupeLotsList(fromContext);
    }

    return dedupeLotsList(
      filterLotsByProductAndLocation(
        readLotsSyncForBusiness(businessId),
        productId,
        location
      )
    );
  } catch (error) {
    return [];
  }
};

/** lots als Array (KV kann Objekt-Map aus Legacy-Migration liefern). */
export function ensureLotsArray(parsed) {
  if (!parsed || typeof parsed !== 'object') {
    return [];
  }
  if (!parsed.lots) {
    assignParsedLotsGuarded(parsed, [], { reason: 'ensureLotsArray_empty' });
  } else if (!Array.isArray(parsed.lots)) {
    assignParsedLotsGuarded(parsed, Object.values(parsed.lots), {
      reason: 'ensureLotsArray_map_to_array'
    });
  }
  return parsed.lots;
}

/** Gleicher Einkaufsbeleg — keine Merge über verschiedene purchaseId. */
export function samePurchaseReceipt(existingLot, lot) {
  const a = existingLot?.purchaseId ?? null;
  const b = lot?.purchaseId ?? null;
  // Both missing is NOT a shared receipt — otherwise distinct lots without
  // purchaseId collapse (e.g. expired + active) and wipe sellable stock.
  if (a == null || a === '' || b == null || b === '') return false;
  return String(a) === String(b);
}

/** Never fold sellable stock into an expired/sold-out/transferred row. */
function lotStatusCompatibleForMerge(a, b) {
  const sa = String(a?.status || LOT_STATUS.ACTIVE).toLowerCase();
  const sb = String(b?.status || LOT_STATUS.ACTIVE).toLowerCase();
  const blocked = new Set([
    LOT_STATUS.EXPIRED,
    LOT_STATUS.SOLD_OUT,
    LOT_STATUS.TRANSFERRED,
    'expired',
    'sold_out',
    'transferred'
  ]);
  if (blocked.has(sa) || blocked.has(sb)) return sa === sb;
  return true;
}

function normalizeLotLocationKey(location) {
  return String(location || 'Principal').trim().toLowerCase();
}

function normalizeLotExpiryKey(expirationDate) {
  if (!expirationDate) return '';
  return String(expirationDate).slice(0, 10);
}

function normalizeLotBatchKey(batchNumber) {
  // Lot-Nr. vergleichen — Transfer-Umschreibungen (`…_T123`) zählen als dieselbe Nr.
  return stripTransferLotNumberSuffix(batchNumber);
}

function lotPricesAndExpiryMatch(a, b) {
  const buyA = Number(a?.purchaseUnitPrice);
  const buyB = Number(b?.purchaseUnitPrice);
  return (
    sameLotMoney(buyA, buyB) &&
    sameLotMoney(
      resolveReceiveSellingUnitPrice(a, buyA),
      resolveReceiveSellingUnitPrice(b, buyB)
    ) &&
    normalizeLotExpiryKey(a?.expirationDate) === normalizeLotExpiryKey(b?.expirationDate)
  );
}

/**
 * Gleiches logisches Lot (Update → kein zweites Lot):
 * gleiche Lot-/Batch-Nr. + Produkt + Location + Preise + Ablauf.
 * Distinct persisted IDs without shared batch/purchaseId are never logical-equal
 * (ghost clones with same batch+qty still collapse in dedupeLotsList).
 */
export function lotsAreSameLogicalLot(a, b) {
  if (!a || !b) return false;
  if (!sameProductId(a.productId, b.productId)) return false;
  if (normalizeLotLocationKey(a.location) !== normalizeLotLocationKey(b.location)) return false;
  if (!lotStatusCompatibleForMerge(a, b)) return false;
  const batchA = normalizeLotBatchKey(a.batchNumber);
  const batchB = normalizeLotBatchKey(b.batchNumber);
  // Different lot numbers are always distinct lots
  if (batchA !== batchB) return false;
  if (!batchA) {
    // No batch number — only merge when same purchase receipt + prices/expiry
    return samePurchaseReceipt(a, b) && lotPricesAndExpiryMatch(a, b);
  }
  // Same lot number → update/merge when prices + expiry match (no second lot)
  return lotPricesAndExpiryMatch(a, b);
}

function recomputeLotValues(lot) {
  const qty = Number(lot.remainingQuantity) || 0;
  const buy = Number(lot.purchaseUnitPrice) || 0;
  const sell = Number(lot.sellingUnitPrice) || 0;
  lot.remainingQuantity = qty;
  lot.totalQuantity = Number(lot.totalQuantity) || qty;
  lot.totalPurchaseValue = qty * buy;
  lot.totalSellingValue = qty * sell;
  lot.potentialProfit = lot.totalSellingValue - lot.totalPurchaseValue;
  return lot;
}

function mergeLotQuantitiesInto(keep, other) {
  keep.remainingQuantity =
    (Number(keep.remainingQuantity) || 0) + (Number(other.remainingQuantity) || 0);
  keep.totalQuantity =
    (Number(keep.totalQuantity) || 0) + (Number(other.totalQuantity) || 0);
  if (other.purchaseId && !keep.purchaseId) {
    keep.purchaseId = String(other.purchaseId);
  }
  const keepT = new Date(keep.updatedAt || 0).getTime();
  const otherT = new Date(other.updatedAt || 0).getTime();
  if (otherT > keepT) keep.updatedAt = other.updatedAt;
  else keep.updatedAt = keep.updatedAt || new Date().toISOString();
  return recomputeLotValues(keep);
}

/**
 * Entfernt doppelte Lots:
 * 1) gleiche Lot-ID → ein Eintrag (neuestes updatedAt, keine Mengen-Summe)
 * 2) gleiche Lot-Nr./Preise/Ablauf am Standort → Mengen zusammenführen
 */
export function dedupeLotsList(lots) {
  const list = Array.isArray(lots) ? lots.filter((l) => l && typeof l === 'object') : [];
  if (list.length <= 1) return list;

  const byId = new Map();
  const withoutId = [];
  for (const lot of list) {
    if (lot.id == null || lot.id === '') {
      withoutId.push(lot);
      continue;
    }
    const key = String(lot.id);
    const prev = byId.get(key);
    if (!prev) {
      byId.set(key, lot);
      continue;
    }
    const prevT = new Date(prev.updatedAt || 0).getTime();
    const nextT = new Date(lot.updatedAt || 0).getTime();
    byId.set(key, nextT >= prevT ? lot : prev);
  }

  const afterId = [...byId.values(), ...withoutId];
  const keepers = [];
  for (const lot of afterId) {
    const matchIdx = keepers.findIndex((k) => lotsAreSameLogicalLot(k, lot));
    if (matchIdx < 0) {
      keepers.push({ ...lot });
      continue;
    }
    if (sameLotId(keepers[matchIdx].id, lot.id)) {
      const prevT = new Date(keepers[matchIdx].updatedAt || 0).getTime();
      const nextT = new Date(lot.updatedAt || 0).getTime();
      if (nextT >= prevT) keepers[matchIdx] = { ...lot };
      continue;
    }
    const keepQty = Number(keepers[matchIdx].remainingQuantity) || 0;
    const lotQty = Number(lot.remainingQuantity) || 0;
    const keepTotal = Number(keepers[matchIdx].totalQuantity) || keepQty;
    const lotTotal = Number(lot.totalQuantity) || lotQty;
    // Same logical lot + same qty → ghost clone (full stock on each row). Keep one, never 2×.
    if (keepQty === lotQty && keepTotal === lotTotal) {
      const prevT = new Date(keepers[matchIdx].updatedAt || 0).getTime();
      const nextT = new Date(lot.updatedAt || 0).getTime();
      if (nextT >= prevT) keepers[matchIdx] = { ...lot };
      continue;
    }
    keepers[matchIdx] = mergeLotQuantitiesInto({ ...keepers[matchIdx] }, lot);
  }
  return keepers;
}

/**
 * @param {object} parsed
 * @param {object} lot
 * @param {{ allowLegacyQtyMutation?: boolean }} [options]
 */
function mergeLotIntoBusinessPayload(parsed, lot, options = {}) {
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('mergeLotIntoBusinessPayload: invalid payload');
  }
  ensureLotsArray(parsed);
  const qtyGuard = {
    reason: 'mergeLotIntoBusinessPayload',
    allowLegacyQtyMutation: options.allowLegacyQtyMutation === true
  };

  const exactMatchIndex = parsed.lots.findIndex((existingLot) =>
    sameLotId(existingLot.id, lot.id)
  );

  if (exactMatchIndex >= 0) {
    const existingLot = parsed.lots[exactMatchIndex];
    if (sameProductId(existingLot.productId, lot.productId) || lot.productId == null) {
      assertLotMergeQuantityAllowed(existingLot, lot, qtyGuard);
      // Update in place — never regenerate id / push a second row
      parsed.lots[exactMatchIndex] = {
        ...existingLot,
        ...lot,
        id: existingLot.id,
        productId: existingLot.productId
      };
    } else {
      // ID collision across products: rewrite incoming id, still prefer logical merge
      const incoming = { ...lot, id: generateLotId() };
      const similarIdx = parsed.lots.findIndex((existing) =>
        lotsAreSameLogicalLot(existing, incoming)
      );
      if (similarIdx >= 0) {
        assertLotMergeQuantityAllowed(parsed.lots[similarIdx], incoming, qtyGuard);
        parsed.lots[similarIdx] = mergeLotQuantitiesInto(
          { ...parsed.lots[similarIdx] },
          incoming
        );
      } else {
        assertLotMergeQuantityAllowed(null, incoming, qtyGuard);
        parsed.lots.push(incoming);
      }
    }
  } else {
    const similarLotIndex = parsed.lots.findIndex((existingLot) =>
      lotsAreSameLogicalLot(existingLot, lot)
    );

    if (similarLotIndex >= 0) {
      assertLotMergeQuantityAllowed(parsed.lots[similarLotIndex], lot, qtyGuard);
      parsed.lots[similarLotIndex] = mergeLotQuantitiesInto(
        { ...parsed.lots[similarLotIndex] },
        lot
      );
    } else {
      assertLotMergeQuantityAllowed(null, lot, qtyGuard);
      parsed.lots.push(lot);
    }
  }

  assignParsedLotsGuarded(parsed, dedupeLotsList(parsed.lots), {
    reason: 'mergeLotIntoBusinessPayload_dedupe'
  });
  return parsed;
}

/**
 * Speichert ein Lot (merged in vollständigen business_*_data — nie nur { lots }).
 * ADR-0013: Prefer domain/inventory/lots.saveLot for new call sites.
 */
export const saveLot = (lot) => {
  try {
    const businessId = getCurrentBusinessId();
    if (!businessId) return false;

    const businessDataKey = businessDataStorageKey(businessId);
    if (!businessDataKey) return false;

    const result = persistBusinessPayloadSync(businessId, (parsed) =>
      mergeLotIntoBusinessPayload(parsed, lot)
    );
    return result.success;
  } catch (error) {
    console.error('[saveLot]', error?.message || error);
    return false;
  }
};

/** Serialisiert mit queueBusinessPayloadWrite (sicher bei parallelen Achats-Schreibungen). */
export async function saveLotAsync(lot) {
  const businessId = getCurrentBusinessId({ allowFallback: false });
  if (!businessId) return false;
  try {
    const updated = await getBusinessPayloadRepository().mutatePayload(
      businessId,
      (parsed) => {
        ensureLotsArray(parsed);
        return mergeLotIntoBusinessPayload(parsed, lot);
      },
      { authoritativeFields: ['lots'] }
    );
    if (Array.isArray(updated?.lots)) {
      seedLotsReadCache(businessId, updated.lots);
    }
    if (lot?.id) {
      try {
        const { enqueueHybridEntitySync } = await import('../services/hybridCatalogSync');
        void enqueueHybridEntitySync(businessId, 'lot', {
          ...lot,
          updatedAt: lot.updatedAt || new Date().toISOString()
        });
      } catch {
        /* hybrid optional */
      }
    }
    return true;
  } catch (error) {
    console.error('[saveLotAsync]', error?.message || error);
    return false;
  }
}

/** Alle Lots in einem einzigen Payload-Write speichern (schneller bei Multi-Produkt-Achats). */
export async function saveLotsBatchAsync(lots, options = {}) {
  const businessId =
    options.businessId || getCurrentBusinessId({ allowFallback: false });
  if (!businessId) return false;
  const list = Array.isArray(lots) ? lots : [];
  if (list.length === 0) return true;
  try {
    // Optimistic warm seed so Stock/POS see new lots before entity index catches up
    try {
      const existing = readLotsSyncForBusiness(businessId) || [];
      const byId = new Map(
        existing.filter((l) => l?.id != null).map((l) => [String(l.id), l])
      );
      for (const lot of list) {
        if (lot?.id != null) byId.set(String(lot.id), lot);
      }
      seedLotsReadCache(businessId, [...byId.values()]);
    } catch {
      /* best-effort */
    }

    const allowLegacyQtyMutation = options.allowLegacyQtyMutation === true;
    const updated = await getBusinessPayloadRepository().mutatePayload(
      businessId,
      (parsed) => {
        ensureLotsArray(parsed);
        for (const lot of list) {
          mergeLotIntoBusinessPayload(parsed, lot, { allowLegacyQtyMutation });
        }
        return parsed;
      },
      { authoritativeFields: ['lots'] }
    );
    if (Array.isArray(updated?.lots)) {
      seedLotsReadCache(businessId, updated.lots);
    }
    // Hybrid: push new/updated lots so cashier PC pulls stock after manager achat
    try {
      const { enqueueHybridEntitySync } = await import('../services/hybridCatalogSync');
      const stamp = new Date().toISOString();
      for (const lot of list) {
        if (!lot?.id) continue;
        void enqueueHybridEntitySync(businessId, 'lot', {
          ...lot,
          updatedAt: lot.updatedAt || stamp
        });
      }
    } catch {
      /* hybrid optional when plan ≠ hybrid */
    }
    // Caller (receiveGoods / Purchases) runs stock recalc which publishes inventory once.
    // Do not publish here — avoids double POS catalog remount on every achat.
    return true;
  } catch (error) {
    console.error('[saveLotsBatchAsync]', error?.message || error);
    return false;
  }
}

/**
 * Produkt-Cueillette → PRICING_METHODS (FIFO | LIFO | FEFO).
 * @param {{ picking?: string, Cueillette?: string } | null} source
 */
export function resolvePickingMethod(source = null) {
  const raw = String(
    source?.picking ?? source?.Cueillette ?? source?.cueillette ?? 'FIFO'
  )
    .trim()
    .toUpperCase();
  if (raw === 'FEFO') return PRICING_METHODS.FEFO;
  if (raw === 'LIFO') return PRICING_METHODS.LIFO;
  return PRICING_METHODS.FIFO;
}

/**
 * Sortiert verkaufbare Lots nach FIFO / LIFO / FEFO.
 */
export function sortLotsForPickingMethod(lots, method = PRICING_METHODS.FIFO) {
  const available = (Array.isArray(lots) ? lots : []).filter((lot) =>
    isLotAvailableForSale(lot)
  );
  const m = resolvePickingMethod({ picking: method });

  if (m === PRICING_METHODS.FEFO) {
    return [...available].sort((a, b) => {
      if (!a.expirationDate && !b.expirationDate) return 0;
      if (!a.expirationDate) return 1;
      if (!b.expirationDate) return -1;
      const byExpiry = new Date(a.expirationDate) - new Date(b.expirationDate);
      if (byExpiry !== 0) return byExpiry;
      return new Date(a.purchaseDate) - new Date(b.purchaseDate);
    });
  }
  if (m === PRICING_METHODS.LIFO) {
    return [...available].sort(
      (a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate)
    );
  }
  return [...available].sort((a, b) => {
    const ta = new Date(a.purchaseDate || a.createdAt || 0).getTime();
    const tb = new Date(b.purchaseDate || b.createdAt || 0).getTime();
    return ta - tb;
  });
}

/**
 * Verteilt Menge linear auf sortierte Lots (reine Funktion).
 */
export function allocateQuantityFromLots(
  lots,
  quantity,
  method = PRICING_METHODS.FIFO,
  productId = null
) {
  const sortedLots = sortLotsForPickingMethod(lots, method);
  const resolvedMethod = resolvePickingMethod({ picking: method });

  let remainingQuantity = Number(quantity);
  let totalCost = 0;
  const usedLots = [];

  for (const lot of sortedLots) {
    if (remainingQuantity <= 0) break;
    const availableQuantity = Number(lot.remainingQuantity);
    const usedQuantity = Math.min(remainingQuantity, availableQuantity);
    totalCost += usedQuantity * Number(lot.purchaseUnitPrice || 0);
    remainingQuantity -= usedQuantity;
    usedLots.push({
      lotId: lot.id,
      productId: productId ?? lot.productId,
      location: lot.location,
      batchNumber: lot.batchNumber,
      quantity: usedQuantity,
      unitPrice: lot.purchaseUnitPrice,
      totalCost: usedQuantity * Number(lot.purchaseUnitPrice || 0),
      expirationDate: lot.expirationDate
    });
  }

  return {
    totalCost,
    usedLots,
    remainingQuantity: Math.max(0, remainingQuantity),
    method: resolvedMethod
  };
}

/**
 * FIFO Preismethode (First In, First Out)
 * Arbeitet linear mit verfügbarem Stock
 */
export const calculateFIFOPricing = (productId, quantity, location = null) => {
  const lots = getLotsForProduct(productId, location);
  return allocateQuantityFromLots(
    lots,
    quantity,
    PRICING_METHODS.FIFO,
    productId
  );
};

/**
 * LIFO Preismethode (Last In, First Out)
 * Arbeitet linear mit verfügbarem Stock
 */
export const calculateLIFOPricing = (productId, quantity, location = null) => {
  const lots = getLotsForProduct(productId, location);
  return allocateQuantityFromLots(
    lots,
    quantity,
    PRICING_METHODS.LIFO,
    productId
  );
};

/**
 * FEFO Preismethode (First Expired, First Out)
 * Arbeitet linear mit verfügbarem Stock
 */
export const calculateFEFOPricing = (productId, quantity, location = null) => {
  const lots = getLotsForProduct(productId, location);
  return allocateQuantityFromLots(
    lots,
    quantity,
    PRICING_METHODS.FEFO,
    productId
  );
};

/**
 * AVERAGE Preismethode (Durchschnittspreis)
 * Arbeitet linear mit verfügbarem Stock
 */
export const calculateAVERAGEPricing = (productId, quantity, location = null) => {
  const lots = getLotsForProduct(productId, location);
  
  // Filtere verfügbare Lots
  const availableLots = lots.filter((lot) => isLotAvailableForSale(lot));

  if (availableLots.length === 0) {
    return {
      totalCost: 0,
      usedLots: [],
      remainingQuantity: quantity,
      method: PRICING_METHODS.AVERAGE
    };
  }

  // Berechne Durchschnittspreis
  const totalQuantity = availableLots.reduce((sum, lot) => sum + lot.remainingQuantity, 0);
  const totalValue = availableLots.reduce((sum, lot) => sum + (lot.remainingQuantity * lot.purchaseUnitPrice), 0);
  const averagePrice = totalValue / totalQuantity;

  const usedQuantity = Math.min(quantity, totalQuantity);
  const totalCost = usedQuantity * averagePrice;

  // Erstelle usedLots basierend auf tatsächlichen Lots (proportional)
  const usedLots = [];
  let remainingToAllocate = usedQuantity;
  
  for (const lot of availableLots) {
    if (remainingToAllocate <= 0) break;
    
    // Berechne proportionalen Anteil für dieses Lot
    const lotProportion = lot.remainingQuantity / totalQuantity;
    const lotUsedQuantity = Math.min(
      Math.round(usedQuantity * lotProportion),
      lot.remainingQuantity,
      remainingToAllocate
    );
    
    if (lotUsedQuantity > 0) {
      usedLots.push({
        lotId: lot.id,
        batchNumber: lot.batchNumber,
        quantity: lotUsedQuantity,
        unitPrice: lot.purchaseUnitPrice,
        totalCost: lotUsedQuantity * lot.purchaseUnitPrice,
        expirationDate: lot.expirationDate
      });
      
      remainingToAllocate -= lotUsedQuantity;
    }
  }
  
  // Falls noch Rest übrig, füge zum ersten Lot hinzu
  if (remainingToAllocate > 0 && usedLots.length > 0) {
    usedLots[0].quantity += remainingToAllocate;
    usedLots[0].totalCost += remainingToAllocate * usedLots[0].unitPrice;
  }


  return {
    totalCost,
    usedLots,
    remainingQuantity: Math.max(0, quantity - totalQuantity),
    method: PRICING_METHODS.AVERAGE
  };
};

export const calculateLotPricing = (productId, quantity, method = PRICING_METHODS.FIFO, location = null) => {
  // Prüfe zuerst, ob Lots für dieses Produkt existieren
  const lots = getLotsForProduct(productId, location);
  
  if (lots.length === 0) {
    // Fallback wenn keine Lots vorhanden
    return {
      totalCost: 0,
      usedLots: [],
      remainingQuantity: quantity,
      method: 'fallback',
      success: false,
      error: 'No lots found for product'
    };
  }
  
  // Verwende Lot-System wenn Lots vorhanden
  switch (method) {
    case PRICING_METHODS.FIFO:
      return calculateFIFOPricing(productId, quantity, location);
    case PRICING_METHODS.LIFO:
      return calculateLIFOPricing(productId, quantity, location);
    case PRICING_METHODS.FEFO:
      return calculateFEFOPricing(productId, quantity, location);
    case PRICING_METHODS.AVERAGE:
      return calculateAVERAGEPricing(productId, quantity, location);
    default:
      return calculateFIFOPricing(productId, quantity, location);
  }
};

// DEPRECATED: updateLotQuantities function removed
// Lot quantity updates at checkout: domain/inventory/consume (saleLotPlanning)
// in StockAsInventorySubclass.jsx to prevent double processing

/**
 * Get stock data from unified structure
 */
// DEPRECATED: getStockData moved to stockCalculationService.js
// Use getStockData from '../services/stockCalculationService' instead

/**
 * Save stock data to unified structure
 */
// DEPRECATED: saveStockData moved to stockCalculationService.js
// Use recalculateAndUpdateStock from '../services/stockCalculationService' instead

/**
 * Aktualisiert STOCK basierend auf LOT-Mengen
 * FIXED: Now uses synchronous stock calculation with proper UI updates
 */
export const updateStockFromLots = async (productId, location = null) => {
  try {
    const businessId = getCurrentBusinessId({ allowFallback: false });
    if (!businessId) {
      return false;
    }

    const { runStockRecalculationAfterMovement } = await import(
      '../domain/inventory/recalculation'
    );
    const orchestration = await runStockRecalculationAfterMovement({
      businessId,
      entries: [{ productId, location }],
      pricingProductIds: [productId],
      source: 'lotUpdate',
      showInventoryOverlay: false
    });

    if (orchestration.success === false) {
      return false;
    }

    const { getStockData } = await import('../services/stockCalculationService');
    const stockData = getStockData(productId, location);
    const result = { success: true, stockData };

    emitStockUpdated({
      productId,
      location,
      stockData: result.stockData,
      source: 'lotUpdate',
      forceRefresh: true,
      timestamp: Date.now()
    });

    if (location) {
      const allLocationStockData = getStockData(productId, null);

      emitStockUpdated({
        productId,
        location: null,
        stockData: allLocationStockData,
        source: 'lotUpdate',
        forceRefresh: true,
        timestamp: Date.now()
      });
    }

    setTimeout(() => {
      const finalStockDetail = {
        productId,
        location,
        stockData: result.stockData,
        source: 'lotUpdateFinal',
        timestamp: Date.now()
      };
      emitStockUpdated(finalStockDetail);
      publishLegacyWindowEvent('inventoryUpdated', finalStockDetail);

      if (location) {
        const allLocationStockData = getStockData(productId, null);
        const allLocDetail = {
          productId,
          location: null,
          stockData: allLocationStockData,
          source: 'lotUpdateFinal',
          timestamp: Date.now()
        };
        emitStockUpdated(allLocDetail);
      }

      emitLotUpdated({
        productId,
        action: 'stockRecalculated',
        location,
        stockData: result.stockData,
        timestamp: Date.now()
      });

      publishForceStockRefresh({
        productId,
        location,
        timestamp: Date.now()
      });
    }, 100);

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Lot-Transfer zwischen Standorten
 */
/**
 * Transferiert Lot zwischen Standorten.
 * @deprecated ADR-0013: Live-Pfad ist domain/inventory/transfer.transferStock
 * (Stock-UI). Diese Funktion bleibt nur als Legacy-Adapter und sollte nicht
 * parallel neue Transfer-Regeln einführen.
 */
/**
 * LOT TRANSFER — LEGACY (ADR-0013)
 * Use domain/inventory/transfer/transferStock. This adapter refuses new writes.
 */
export const transferLot = () => {
  throw new Error(
    'lotSystem.transferLot is removed (ADR-0013). Use domain/inventory/transfer/transferStock.'
  );
};

/**
 * Aktualisiert Lot-Quantity.
 * ADR-0013: Prefer domain/inventory/lots.updateLotQuantity.
 */
export const updateLotQuantity = async (lotId, newQuantity) => {
  try {
    const resolvedBusinessId =
      getCurrentBusinessId({ allowFallback: false }) ||
      businessIdFromUser(parseKvCurrentUser());
    if (!resolvedBusinessId) {
      return { success: false, error: 'No business ID found' };
    }

    let savedLot = null;
    let oldQuantity = 0;

    await getBusinessPayloadRepository().mutatePayload(
      resolvedBusinessId,
      (parsed) => {
        ensureLotsArray(parsed);
        const lotIndex = parsed.lots.findIndex((lot) => sameLotId(lot.id, lotId));
        if (lotIndex === -1) {
          throw new Error('Lot not found');
        }

        const lot = { ...parsed.lots[lotIndex] };
        oldQuantity = lot.remainingQuantity;
        const nextQty = Number(newQuantity);
        const delta = nextQty - Number(oldQuantity || 0);

        if (delta !== 0) {
          // ADR-0016 Phase 4: adjustment movement projects lot remaining
          const [movement] = appendStockMovements(
            parsed,
            [buildAdjustmentMovement(lot, delta, 'manual_lot_quantity')],
            {
              businessId: resolvedBusinessId,
              projectLots: true,
              actorId: 'manual:lot_quantity'
            }
          );
          savedLot =
            parsed.lots.find((l) => sameLotId(l.id, lot.id)) ||
            parsed.lots.find((l) => lotsAreSameLogicalLot(l, lot)) ||
            lot;
          if (movement?.lotId) {
            const projected = parsed.lots.find((l) => sameLotId(l.id, movement.lotId));
            if (projected) savedLot = projected;
          }
        } else {
          savedLot = lot;
        }
        assignParsedLotsGuarded(parsed, dedupeLotsList(parsed.lots), {
          reason: 'updateLotQuantity_dedupe'
        });
        return parsed;
      },
      { authoritativeFields: ['lots', ...INVENTORY_LEDGER_AUTHORITATIVE_FIELDS] }
    );

    updateStockFromLots(savedLot.productId, savedLot.location);
    
    // Dispatch lot update event
    emitLotUpdated({
      productId: savedLot.productId,
      lotId,
      location: savedLot.location,
      type: 'sale_reduction',
      quantity: oldQuantity - newQuantity,
      newQuantity: Number(newQuantity)
    });

    return {
      success: true,
      lot: savedLot,
      oldQuantity,
      newQuantity: Number(newQuantity)
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Aktualisiert Lot-Location
 */
export const updateLotLocation = (lotId, newLocation) => {
  try {
    const user = parseKvCurrentUser();
    if (!user) return { success: false, error: 'No user found' };

    const businessId = businessIdFromUser(user);

    if (!businessId) return { success: false, error: 'No business ID found' };

    let resultLot = null;
    let oldLocation = null;

    const writeResult = persistBusinessPayloadSync(businessId, (parsed) => {
      ensureLotsArray(parsed);
      assignParsedLotsGuarded(parsed, dedupeLotsList(parsed.lots), {
        reason: 'updateLotLocation_dedupe_pre'
      });
      const lotIndex = parsed.lots.findIndex((lot) => sameLotId(lot.id, lotId));
      if (lotIndex === -1) throw new Error('Lot not found');

      const lot = { ...parsed.lots[lotIndex] };
      oldLocation = lot.location;
      lot.location = newLocation;
      lot.updatedAt = new Date().toISOString();
      parsed.lots[lotIndex] = lot;
      assignParsedLotsGuarded(parsed, dedupeLotsList(parsed.lots), {
        reason: 'updateLotLocation_dedupe'
      });
      resultLot =
        parsed.lots.find((l) => sameLotId(l.id, lot.id)) ||
        parsed.lots.find((l) => lotsAreSameLogicalLot(l, lot)) ||
        lot;
      return parsed;
    });

    if (!writeResult.success) {
      return { success: false, error: writeResult.error || 'Write failed' };
    }
    if (!resultLot) {
      return { success: false, error: 'Lot location update produced no result' };
    }

    updateStockFromLots(resultLot.productId, oldLocation);
    updateStockFromLots(resultLot.productId, newLocation);

    return {
      success: true,
      lot: resultLot,
      oldLocation,
      newLocation
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Aktualisiert Lot-Preise
 */
export const updateLotPrice = (lotId, newPurchasePrice, newSellingPrice) => {
  try {
    const user = parseKvCurrentUser();
    if (!user) return { success: false, error: 'No user found' };

    const businessId = businessIdFromUser(user);

    if (!businessId) return { success: false, error: 'No business ID found' };

    let resultLot = null;
    let oldPurchasePrice;
    let oldSellingPrice;

    const writeResult = persistBusinessPayloadSync(businessId, (parsed) => {
      ensureLotsArray(parsed);
      assignParsedLotsGuarded(parsed, dedupeLotsList(parsed.lots), {
        reason: 'updateLotPrice_dedupe_pre'
      });
      const lotIndex = parsed.lots.findIndex((lot) => sameLotId(lot.id, lotId));
      if (lotIndex === -1) throw new Error('Lot not found');

      const lot = { ...parsed.lots[lotIndex] };
      oldPurchasePrice = lot.purchaseUnitPrice;
      oldSellingPrice = lot.sellingUnitPrice;

      if (newPurchasePrice !== undefined) {
        lot.purchaseUnitPrice = Number(newPurchasePrice);
      }
      if (newSellingPrice !== undefined) {
        lot.sellingUnitPrice = Number(newSellingPrice);
      }

      lot.updatedAt = new Date().toISOString();
      lot.totalPurchaseValue = lot.remainingQuantity * lot.purchaseUnitPrice;
      lot.totalSellingValue = lot.remainingQuantity * lot.sellingUnitPrice;
      lot.potentialProfit = lot.totalSellingValue - lot.totalPurchaseValue;

      parsed.lots[lotIndex] = lot;
      assignParsedLotsGuarded(parsed, dedupeLotsList(parsed.lots), {
        reason: 'updateLotPrice_dedupe'
      });
      resultLot =
        parsed.lots.find((l) => sameLotId(l.id, lot.id)) ||
        parsed.lots.find((l) => lotsAreSameLogicalLot(l, lot)) ||
        lot;
      return parsed;
    });

    if (!writeResult.success) {
      return { success: false, error: writeResult.error || 'Write failed' };
    }
    if (!resultLot) {
      return { success: false, error: 'Lot price update produced no result' };
    }

    updateStockFromLots(resultLot.productId, resultLot.location);

    return {
      success: true,
      lot: resultLot,
      oldPurchasePrice,
      newPurchasePrice: Number(newPurchasePrice),
      oldSellingPrice,
      newSellingPrice: Number(newSellingPrice)
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};


/**
 * Pure patch of lot fields (no I/O) — used by atomic achat-edit commits.
 * Quantity must not be patched here (ADR-0016) — use ADJUSTMENT movements.
 * @param {object} lot
 * @param {object} updates — same shape as updateLotAttributes updates
 * @returns {object|null}
 */
export function patchLotWithAttributeUpdates(lot, updates = {}) {
  if (!lot) return null;
  const next = { ...lot };

  if (updates.quantity !== undefined) {
    // ADR-0016 Phase 5: remainingQuantity only via movement projection
    assertLotQuantityMutationAllowed({
      reason: 'patchLotWithAttributeUpdates.quantity',
      allowLegacyQtyMutation: updates.allowLegacyQtyMutation === true
    });
    next.remainingQuantity = Number(updates.quantity);
    if (updates.isNewLot) {
      next.totalQuantity = Number(updates.quantity);
    }
  }
  if (updates.totalQuantity !== undefined) {
    next.totalQuantity = Number(updates.totalQuantity);
  }
  if (updates.purchasePrice !== undefined) {
    next.purchaseUnitPrice = Number(updates.purchasePrice);
  }
  if (updates.sellingPrice !== undefined) {
    next.sellingUnitPrice = Number(updates.sellingPrice);
  }
  if (updates.location !== undefined) {
    next.location = updates.location;
  }
  if (updates.expirationDate !== undefined) {
    next.expirationDate = updates.expirationDate || null;
  }
  if (updates.status !== undefined) {
    next.status = updates.status;
  }

  const CORE_KEYS = new Set([
    'quantity',
    'totalQuantity',
    'purchasePrice',
    'sellingPrice',
    'location',
    'expirationDate',
    'status',
    'isNewLot'
  ]);
  for (const [key, value] of Object.entries(updates || {})) {
    if (CORE_KEYS.has(key) || value === undefined) continue;
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      next[key] &&
      typeof next[key] === 'object' &&
      !Array.isArray(next[key])
    ) {
      next[key] = { ...next[key], ...value };
    } else {
      next[key] = value;
    }
  }

  next.updatedAt = new Date().toISOString();
  next.totalPurchaseValue = next.remainingQuantity * next.purchaseUnitPrice;
  next.totalSellingValue = next.remainingQuantity * next.sellingUnitPrice;
  next.potentialProfit = next.totalSellingValue - next.totalPurchaseValue;

  if (updates.status === LOT_STATUS.EXPIRED) {
    next.status = LOT_STATUS.EXPIRED;
  } else if (next.remainingQuantity <= 0) {
    next.status = LOT_STATUS.SOLD_OUT;
  } else if (next.status === LOT_STATUS.SOLD_OUT) {
    next.status = LOT_STATUS.ACTIVE;
  }

  return next;
}

/**
 * Aktualisiert Lot-Attribute (Menge, Preise, Location, Expiry, Status).
 * ADR-0013: Prefer import from domain/inventory/lots for new call sites.
 */
function shouldUseSingleRowLotPutSync() {
  try {
    return isEntityStoreEnabled() && isEntityStoreNoKvBlobEnabled();
  } catch {
    return false;
  }
}

/**
 * Phase C — attribute edit: non-qty via patch; quantity via ADJUSTMENT movement + projection.
 */
function persistLotAttributeUpdate(businessId, lotId, updates, fallbackLot) {
  const id = normalizeBusinessStorageId(businessId);
  if (!id) return { success: false, error: 'No business ID found' };

  const rawUpdates = updates && typeof updates === 'object' ? { ...updates } : {};
  const hasQty = Object.prototype.hasOwnProperty.call(rawUpdates, 'quantity');
  const targetQty = hasQty ? Number(rawUpdates.quantity) : null;
  const nonQtyUpdates = { ...rawUpdates };
  delete nonQtyUpdates.quantity;

  const current = loadBusinessPayloadBest(id) || {};
  const lotsSource = resolveLotsForSyncMutator(id, current.lots);
  const dryBase = {
    ...current,
    lots: cloneLotsForDryRun(lotsSource)
  };

  let resultLot = null;
  let oldValues = null;
  let syncUpdated;
  try {
    ensureLotsArray(dryBase);
    let lotIndex = dryBase.lots.findIndex((lot) => sameLotId(lot.id, lotId));
    if (lotIndex === -1 && fallbackLot && sameLotId(fallbackLot.id, lotId)) {
      dryBase.lots.push({ ...fallbackLot });
      lotIndex = dryBase.lots.length - 1;
    }
    if (lotIndex === -1) throw new Error('Lot not found');

    oldValues = {
      quantity: dryBase.lots[lotIndex].remainingQuantity,
      purchasePrice: dryBase.lots[lotIndex].purchaseUnitPrice,
      sellingPrice: dryBase.lots[lotIndex].sellingUnitPrice,
      location: dryBase.lots[lotIndex].location,
      expirationDate: dryBase.lots[lotIndex].expirationDate
    };

    if (Object.keys(nonQtyUpdates).length > 0) {
      const lot = patchLotWithAttributeUpdates(dryBase.lots[lotIndex], nonQtyUpdates);
      if (!lot) throw new Error('Lot update failed');
      dryBase.lots[lotIndex] = lot;
    }

    if (hasQty && Number.isFinite(targetQty)) {
      const lotBefore = dryBase.lots[lotIndex];
      const delta = targetQty - (Number(lotBefore.remainingQuantity) || 0);
      if (Math.abs(delta) > 1e-9) {
        appendStockMovements(
          dryBase,
          [buildAdjustmentMovement(lotBefore, delta, 'manual_lot_quantity')],
          { businessId: id, projectLots: true }
        );
      }
    }

    dryBase.lots = dedupeLotsList(dryBase.lots);
    resultLot =
      dryBase.lots.find((l) => sameLotId(l.id, lotId)) ||
      dryBase.lots.find((l) => fallbackLot && lotsAreSameLogicalLot(l, fallbackLot)) ||
      dryBase.lots[lotIndex];
    syncUpdated = dryBase;
  } catch (err) {
    return { success: false, error: err?.message || String(err) };
  }

  try {
    if (Array.isArray(syncUpdated.lots)) {
      seedLotsReadCache(id, syncUpdated.lots);
    }
  } catch {
    /* best-effort */
  }

  let writePromise;
  if (shouldUseSingleRowLotPutSync() && resultLot && !hasQty) {
    writePromise = queueEntityItemUpsert(id, 'lots', resultLot, {
      nextCollection: syncUpdated.lots
    }).catch((err) => {
      console.warn('[lotSystem] queueEntityItemUpsert failed:', err?.message || err);
      return null;
    });
  } else {
    writePromise = queueBusinessPayloadWrite(
      id,
      (parsed) => {
        const base = { ...parsed };
        const overlayLots = resolveLotsForSyncMutator(id, base.lots);
        if (!base.lots?.length || overlayLots.length > (base.lots?.length || 0)) {
          base.lots = cloneLotsForDryRun(overlayLots);
        }
        ensureLotsArray(base);
        let lotIndex = base.lots.findIndex((lot) => sameLotId(lot.id, lotId));
        if (lotIndex === -1 && fallbackLot && sameLotId(fallbackLot.id, lotId)) {
          base.lots.push({ ...fallbackLot });
          lotIndex = base.lots.length - 1;
        }
        if (lotIndex === -1) throw new Error('Lot not found');

        if (Object.keys(nonQtyUpdates).length > 0) {
          const lot = patchLotWithAttributeUpdates(base.lots[lotIndex], nonQtyUpdates);
          if (!lot) throw new Error('Lot update failed');
          base.lots[lotIndex] = lot;
        }

        if (hasQty && Number.isFinite(targetQty)) {
          const lotBefore = base.lots[lotIndex];
          const delta = targetQty - (Number(lotBefore.remainingQuantity) || 0);
          if (Math.abs(delta) > 1e-9) {
            appendStockMovements(
              base,
              [buildAdjustmentMovement(lotBefore, delta, 'manual_lot_quantity')],
              { businessId: id, projectLots: true }
            );
          }
        }

        assignParsedLotsGuarded(base, dedupeLotsList(base.lots), {
          reason: 'updateLotAttributes_persist'
        });
        return base;
      },
      {
        authoritativeFields: hasQty
          ? ['lots', ...INVENTORY_LEDGER_AUTHORITATIVE_FIELDS]
          : ['lots']
      }
    ).catch((err) => {
      console.warn('[lotSystem] queueBusinessPayloadWrite failed:', err?.message || err);
      return null;
    });
  }

  if (resultLot && writePromise) {
    void Promise.resolve(writePromise).then((wr) => {
      if (wr === null) return;
      import('../services/hybridCatalogSync')
        .then(({ enqueueHybridEntitySync }) =>
          enqueueHybridEntitySync(id, 'lot', {
            ...resultLot,
            updatedAt: resultLot.updatedAt || new Date().toISOString()
          })
        )
        .catch(() => {});
    });
  }

  return {
    success: true,
    parsed: syncUpdated,
    writePromise,
    resultLot,
    oldValues
  };
}

export const updateLotAttributes = (lotId, updates, options = {}) => {
  try {
    const user = parseKvCurrentUser();
    const businessId =
      normalizeBusinessStorageId(options.businessId) ||
      businessIdFromUser(user) ||
      getCurrentBusinessId({ allowFallback: false });

    if (!businessId) return { success: false, error: 'No business ID found' };

    const fallbackLot = options.fallbackLot || null;
    const writeResult = persistLotAttributeUpdate(
      businessId,
      lotId,
      updates,
      fallbackLot
    );

    if (!writeResult.success) {
      return { success: false, error: writeResult.error || 'Write failed' };
    }
    const resultLot = writeResult.resultLot;
    const oldValues = writeResult.oldValues;
    if (!resultLot) {
      return { success: false, error: 'Lot update produced no result' };
    }

    updateStockFromLots(resultLot.productId, resultLot.location);
    clearLotCache(resultLot.productId);

    dispatchLotUpdateOptimized(resultLot.productId, 'update', {
      lotId,
      oldValues,
      newValues: {
        quantity: resultLot.remainingQuantity,
        purchasePrice: resultLot.purchaseUnitPrice,
        sellingPrice: resultLot.sellingUnitPrice,
        location: resultLot.location,
        expirationDate: resultLot.expirationDate
      }
    });

    // One cache invalidate — stock recalc / dispatchLotUpdate already publish inventory once.
    // Do not fire extra inventoryUpdated here (POS catalog remount storm).
    import('../utils/cacheUpdateTrigger').then(({ triggerCacheUpdateAfterLotUpdate }) => {
      triggerCacheUpdateAfterLotUpdate(resultLot);
    });

    // After durable write: seed warm cache only (no second inventoryUpdated).
    if (writeResult.writePromise) {
      void writeResult.writePromise.then((payload) => {
        try {
          const lots =
            (Array.isArray(payload?.lots) && payload.lots) ||
            (Array.isArray(payload?.nextCollection) && payload.nextCollection) ||
            null;
          if (lots) {
            seedLotsReadCache(businessId, lots);
          }
        } catch {
          /* best-effort */
        }
      });
    }

    return {
      success: true,
      lot: resultLot,
      oldValues,
      newValues: {
        quantity: resultLot.remainingQuantity,
        purchasePrice: resultLot.purchaseUnitPrice,
        sellingPrice: resultLot.sellingUnitPrice,
        location: resultLot.location,
        expirationDate: resultLot.expirationDate
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Supprime un lot du stock (persistant).
 * ADR-0013: Prefer domain/inventory/lots.deleteLotById.
 */
export const deleteLotById = (lotId) => {
  try {
    const user = parseKvCurrentUser();
    if (!user) return { success: false, error: 'No user found' };

    const businessId = businessIdFromUser(user);
    if (!businessId) return { success: false, error: 'No business ID found' };

    let removedLot = null;

    const writeResult = persistBusinessPayloadSync(businessId, (parsed) => {
      ensureLotsArray(parsed);
      assignParsedLotsGuarded(parsed, dedupeLotsList(parsed.lots), {
        reason: 'deleteLotById_dedupe_pre'
      });
      const lotIndex = parsed.lots.findIndex((lot) => sameLotId(lot.id, lotId));
      if (lotIndex === -1) throw new Error('Lot not found');
      removedLot = { ...parsed.lots[lotIndex] };
      const rem = Number(removedLot.remainingQuantity) || 0;
      if (Math.abs(rem) > 1e-9) {
        // ADR-0016: zero via adjustment projection before drop
        appendStockMovements(
          parsed,
          [buildAdjustmentMovement(removedLot, -rem, 'delete_lot_zero')],
          { businessId, projectLots: true }
        );
      }
      const next = (parsed.lots || []).filter((lot) => !sameLotId(lot.id, lotId));
      assignParsedLotsGuarded(parsed, next, { reason: 'deleteLotById' });
      return parsed;
    });

    if (!writeResult.success) {
      return { success: false, error: writeResult.error || 'Write failed' };
    }

    if (removedLot?.productId) {
      updateStockFromLots(removedLot.productId, removedLot.location);
      clearLotCache(removedLot.productId);
      dispatchLotUpdateOptimized(removedLot.productId, 'delete', { lotId });
    }

    if (removedLot) {
      void import('../services/hybridDeleteSync')
        .then(({ enqueueHybridEntityDelete }) =>
          enqueueHybridEntityDelete(businessId, 'lot', removedLot)
        )
        .catch(() => {});
    }

    return { success: true, lot: removedLot };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * HYBRID PERFORMANCE LAYER: Caching und Optimierungen
 */
const calculationCache = new Map();
const eventQueue = [];
let batchTimeout = null;

// Cache-Management
const getCacheKey = (productId, location, functionName) => {
  return `${functionName}_${productId}_${location || 'all'}`;
};

// clearCache function moved to clearLotCache export

// Debounced Event Dispatch
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

/**
 * HYBRID: Optimierte Berechnungsfunktionen mit Caching
 */
export const getProductValueOptimized = (productId, location = null) => {
  const cacheKey = getCacheKey(productId, location, 'getProductValue');
  
  if (calculationCache.has(cacheKey)) {
    return calculationCache.get(cacheKey);
  }
  
  const value = getProductValue(productId, location);
  calculationCache.set(cacheKey, value);
  return value;
};

export const getProductPotentialProfitOptimized = (productId, location = null) => {
  const cacheKey = getCacheKey(productId, location, 'getProductPotentialProfit');
  
  if (calculationCache.has(cacheKey)) {
    return calculationCache.get(cacheKey);
  }
  
  const profit = getProductPotentialProfit(productId, location);
  calculationCache.set(cacheKey, profit);
  return profit;
};

/**
 * HYBRID: Batch-Update-System für Performance
 */
export const updateLotAttributesBatched = (lotId, updates) => {
  eventQueue.push({ lotId, updates, timestamp: Date.now() });

  if (batchTimeout) clearTimeout(batchTimeout);
  batchTimeout = setTimeout(() => {
    processBatchUpdates();
  }, 50); // 50ms batch window
};

/** One payload write for all queued lot patches (later updates per lotId win). */
const processBatchUpdates = () => {
  if (eventQueue.length === 0) return;

  const queued = eventQueue.splice(0, eventQueue.length);
  batchTimeout = null;

  const byLotId = new Map();
  for (const { lotId, updates } of queued) {
    if (lotId == null) continue;
    const prev = byLotId.get(lotId) || {};
    byLotId.set(lotId, { ...prev, ...(updates || {}) });
  }
  if (byLotId.size === 0) return;

  const user = parseKvCurrentUser();
  const businessId =
    businessIdFromUser(user) || getCurrentBusinessId({ allowFallback: false });
  if (!businessId) return;

  /** @type {Map<string, Set<string|null>>} productId → locations */
  const touched = new Map();
  const writeResult = persistBusinessPayloadSync(businessId, (parsed) => {
    ensureLotsArray(parsed);
    for (const [lotId, updates] of byLotId) {
      const lotIndex = parsed.lots.findIndex((lot) => sameLotId(lot.id, lotId));
      if (lotIndex === -1) continue;
      const before = parsed.lots[lotIndex];
      const raw = updates && typeof updates === 'object' ? { ...updates } : {};
      const hasQty = Object.prototype.hasOwnProperty.call(raw, 'quantity');
      const targetQty = hasQty ? Number(raw.quantity) : null;
      const nonQty = { ...raw };
      delete nonQty.quantity;

      if (Object.keys(nonQty).length > 0) {
        const lot = patchLotWithAttributeUpdates(before, nonQty);
        if (!lot) continue;
        parsed.lots[lotIndex] = lot;
      }

      if (hasQty && Number.isFinite(targetQty)) {
        const lotBefore = parsed.lots[lotIndex];
        const delta = targetQty - (Number(lotBefore.remainingQuantity) || 0);
        if (Math.abs(delta) > 1e-9) {
          appendStockMovements(
            parsed,
            [buildAdjustmentMovement(lotBefore, delta, 'manual_lot_quantity')],
            { businessId, projectLots: true }
          );
        }
      }

      const lot = parsed.lots.find((l) => sameLotId(l.id, lotId)) || parsed.lots[lotIndex];
      if (lot?.productId == null) continue;
      const key = String(lot.productId);
      if (!touched.has(key)) touched.set(key, new Set());
      touched.get(key).add(before?.location ?? null);
      touched.get(key).add(lot.location ?? null);
    }
    assignParsedLotsGuarded(parsed, dedupeLotsList(parsed.lots), {
      reason: 'updateLotAttributesBatched_dedupe'
    });
    return parsed;
  });

  if (!writeResult.success) return;

  touched.forEach((locations, productId) => {
    clearLotCache(productId);
    locations.forEach((location) => {
      updateStockFromLots(productId, location);
    });
    dispatchLotUpdateOptimized(productId, 'batch_update', {
      updateCount: byLotId.size,
      lots: getLotsForProduct(productId, null)
    });
  });
};

/**
 * ZENTRALE UPDATE-PROPAGATION
 * Zentrale Funktion für alle Lot-Updates mit automatischer Cache-Verwaltung
 */
export const dispatchCentralUpdate = (productId, action = 'update', data = {}) => {
  
  // Clear cache for this product to ensure fresh data
  clearLotCache(productId);
  
  // Dispatch optimized event
  dispatchLotUpdateOptimized(productId, action, data);
};

/**
 * HYBRID: Optimierte Event-Dispatch mit Debouncing
 */
const debouncedDispatch = debounce((productId, action, data) => {
  try {
    // Calculate current values centrally (with cache)
    const totalStock = getProductQuantityOptimized(productId, null);
    const totalValue = getProductValueOptimized(productId, null);
    const totalPotentialProfit = getProductPotentialProfitOptimized(productId, null);
    const lots = getLotsForProduct(productId, null);
    
    publishLotUpdated({
      productId,
      action,
      totalStock,
      totalValue,
      totalPotentialProfit,
      lots,
      ...data
    });
    
  } catch (error) {
  }
}, 100);

export const dispatchLotUpdateOptimized = (productId, action, data = {}) => {
  debouncedDispatch(productId, action, data);
};

/**
 * ZENTRALE FUNKTION: Dispatcht Lot-Update Events (Legacy - wird durch Optimized ersetzt)
 */
export const dispatchLotUpdate = (productId, action, data = {}) => {
  // Redirect to optimized version
  dispatchLotUpdateOptimized(productId, action, data);
};

/**
 * CACHE MANAGEMENT: Export cache functions for external use
 */
export const clearLotCache = (productId = null) => {
  if (productId) {
    // Clear cache for specific product
    for (const [key] of calculationCache) {
      if (key.includes(`_${productId}_`)) {
        calculationCache.delete(key);
      }
    }
  } else {
    // Clear entire cache
    calculationCache.clear();
  }
};

export const getCacheStats = () => ({
  size: calculationCache.size,
  keys: Array.from(calculationCache.keys())
});

/**
 * Migration von altem zu neuem System
 */
export const migrateToUnifiedStructure = () => {
  try {
    const user = parseKvCurrentUser();
    if (!user) return { success: false, error: 'No user found' };

    const businessId = businessIdFromUser(user);

    if (!businessId) return { success: false, error: 'No business ID found' };

    const parsed = loadBusinessPayloadBest(businessId);
    const hasLegacyStockKeys = kvKeysStartingWith('stock_data_').length > 0;
    if (!parsed && !hasLegacyStockKeys) {
      return { success: false, error: 'No business data found' };
    }

    const working = parsed ? { ...parsed } : {};

    // Migrate lots from array to object structure
    if (Array.isArray(working.lots)) {
      const lotsObject = {};
      working.lots.forEach((lot) => {
        lotsObject[lot.id] = lot;
      });
      working.lots = lotsObject;
    }
    
    // Migrate stock data from separate keys to unified structure
    const stockDataObject = {};
    kvKeysStartingWith('stock_data_').forEach((key) => {
      const raw = kvGetRaw(key);
      if (!raw) return;
      try {
        const parsedStock = JSON.parse(raw);
        const productId = key.split('_')[2];
        const location = key.split('_')[3];
        const stockKey = `${productId}_${location}`;
        stockDataObject[stockKey] = {
          productId: parseInt(productId, 10),
          location,
          ...parsedStock
        };
        kvRemove(key);
      } catch {
        /* ignore malformed */
      }
    });
    working.stockData = { ...(working.stockData || {}), ...stockDataObject };

    const wr = persistBusinessPayloadSync(businessId, () => working, ['lots', 'stockData']);
    if (!wr.success) return { success: false, error: wr.error || 'Write failed' };

    return { success: true, message: 'Migration completed successfully' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Flacht Achats-Belege (mit products[]-Array) zu Zeilen für Lot-Erstellung ab.
 * @param {object[]|object} purchases
 * @returns {object[]}
 */
export function flattenPurchaseLineItems(purchases) {
  const lines = [];
  normalizePurchasesField(purchases).forEach((purchase, purchaseIndex) => {
    // Soft-voided / cancelled achats do not contribute intake lines
    if (
      purchase?.voided === true ||
      (() => {
        const s = String(purchase?.status || '')
          .trim()
          .toLowerCase();
        return (
          s === 'voided' ||
          s === 'cancelled' ||
          s === 'canceled' ||
          s === 'storno'
        );
      })()
    ) {
      return;
    }
    const nested =
      (Array.isArray(purchase.products) && purchase.products) ||
      (Array.isArray(purchase.items) && purchase.items) ||
      null;

    if (nested && nested.length > 0) {
      nested.forEach((item, itemIndex) => {
        if (item?.productId == null && item?.id == null) return;
        if (item?.voided === true) return;
        lines.push({
          ...item,
          productId: item.productId ?? item.id,
          quantity: Number(item.quantity ?? item.receivedQuantity ?? item.qty ?? 0),
          purchaseUnitPrice: Number(
            item.purchaseUnitPrice ?? item.unitPrice ?? item.price ?? purchase.purchaseUnitPrice ?? 0
          ),
          sellingPrice: Number(
            item.sellingPrice ?? item.sellingUnitPrice ?? item.price ?? purchase.sellingPrice ?? 0
          ),
          purchaseId: purchase.purchaseId ?? purchase.id ?? item.purchaseId ?? null,
          supplierId: purchase.supplierId ?? item.supplierId ?? purchase.supplier ?? null,
          supplier: purchase.supplier ?? item.supplier ?? purchase.supplierId ?? '',
          location: item.location ?? purchase.location ?? 'Principal',
          date: purchase.date ?? purchase.deliveryDate ?? purchase.purchaseDate ?? item.date,
          expirationDate: item.expirationDate ?? purchase.expirationDate ?? null,
          id: item.id ?? `${purchase.purchaseId ?? purchase.id ?? purchaseIndex}_${itemIndex}`
        });
      });
      return;
    }

    if (purchase.productId != null) {
      lines.push({
        ...purchase,
        productId: purchase.productId,
        quantity: Number(purchase.quantity ?? purchase.receivedQuantity ?? 0),
        purchaseUnitPrice: Number(purchase.purchaseUnitPrice ?? purchase.unitPrice ?? 0)
      });
    }
  });
  return lines.filter((line) => line.productId != null && Number(line.quantity) > 0);
}

/**
 * Baut receiveGoods-Jobs aus Einkäufen (ADR-0013 — keine createLot-Persistenz hier).
 * @param {object[]|object} purchases
 * @returns {Array<{ purchaseId: string, purchaseDate: string, line: object }>}
 */
export function buildReceiveJobsFromPurchases(purchases) {
  const flatPurchases = flattenPurchaseLineItems(purchases);
  const lotGroups = {};

  flatPurchases.forEach((purchase) => {
    const receiptId = purchase.purchaseId || null;
    const key = receiptId
      ? `${receiptId}_${purchase.productId}_${purchase.expirationDate || 'no-exp'}`
      : `${purchase.productId}_${purchase.expirationDate || purchase.date}`;
    if (!lotGroups[key]) {
      lotGroups[key] = {
        productId: purchase.productId,
        purchaseId: receiptId,
        expirationDate: purchase.expirationDate || purchase.date,
        purchases: [],
        totalQuantity: 0
      };
    }
    lotGroups[key].purchases.push(purchase);
    lotGroups[key].totalQuantity += Number(purchase.quantity || 0);
  });

  const jobs = [];
  Object.values(lotGroups).forEach((group) => {
    if (group.purchases.length === 0) return;

    const totalValue = group.purchases.reduce(
      (sum, p) => sum + Number(p.quantity || 0) * Number(p.purchaseUnitPrice || 0),
      0
    );
    const averagePrice = totalValue / group.totalQuantity;

    const sellingPrices = group.purchases
      .map((p) => p.sellingPrice || p.sellingUnitPrice)
      .filter((price) => price && Number(price) > 0);
    const averageSellingPrice =
      sellingPrices.length > 0
        ? sellingPrices.reduce((sum, price) => sum + Number(price), 0) / sellingPrices.length
        : averagePrice;

    const first = group.purchases[0];
    const purchaseId =
      group.purchaseId ||
      first?.purchaseId ||
      first?.id ||
      `mig_${group.productId}_${String(group.expirationDate || 'no-exp')}`;

    const packagingType = first.packagingType || first.packaging || null;
    const productsPerPackage =
      first.productsPerPackage ?? first.unitsPerPack ?? first.itemsPerBox ?? null;
    const numberOfPackages = first.numberOfPackages ?? first.packCount ?? first.numberOfBoxes ?? null;
    const packagePrice = first.packagePrice != null ? first.packagePrice : null;

    jobs.push({
      purchaseId: String(purchaseId),
      purchaseDate: first.date || first.purchaseDate || new Date().toISOString(),
      line: {
        productId: group.productId,
        quantity: group.totalQuantity,
        purchaseUnitPrice: averagePrice,
        sellingUnitPrice: averageSellingPrice,
        expirationDate: group.expirationDate || null,
        location: first.location || 'Principal',
        supplier: first.supplier || '',
        ...(packagingType ? { packagingType } : {}),
        ...(productsPerPackage != null ? { productsPerPackage } : {}),
        ...(numberOfPackages != null ? { numberOfPackages } : {}),
        ...(packagePrice != null ? { packagePrice } : {}),
        ...(first.quantityUom ? { quantityUom: first.quantityUom } : {})
      }
    });
  });

  return jobs;
}

/**
 * Aktualisiert Lesecaches + instantCache nach Lot-Änderungen (Stock/Inventaire UI).
 * @param {string} businessId
 * @param {object[]} lots
 */
export async function refreshInventoryUiAfterLotsChange(businessId, lots, options = {}) {
  if (!businessId) return;

  const { refreshStockDataReadCache } = await import('./stockDataReadSource');
  const lotsArray = Array.isArray(lots) ? lots : normalizeLotsField(lots);

  const entries =
    Array.isArray(options.entries) && options.entries.length > 0
      ? options.entries
      : lotsArray
          .filter((lot) => lot?.productId)
          .map((lot) => ({
            productId: lot.productId,
            location: lot.location ?? null
          }));

  const { runStockRecalculationAfterMovement } = await import(
    '../domain/inventory/recalculation'
  );

  const recalcResult = await runStockRecalculationAfterMovement({
    businessId,
    entries,
    lots: lotsArray,
    source: options.source || 'lotsRefresh',
    showInventoryOverlay: options.showInventoryOverlay ?? false
  });

  await refreshStockDataReadCache(businessId);

  // Orchestrator already warms stock (or marks dirty on checkout). Avoid a second sync ensureWarm.
  if (!recalcResult?.stockWarmed) {
    try {
      const { afterStockMovementCommit } = await import('../services/cacheReadModel');
      afterStockMovementCommit(options.source === 'expiry' ? 'expiry_processed' : 'lot_updated');
    } catch {
      /* facade optional during boot races */
    }
  }

  emitStockUpdated({
    source: options.source || 'lotsRefresh',
    forceRefresh: true,
    timestamp: Date.now()
  });

  // Checkout already wrote lots into React via setLotsOnly — skip inventoryUpdated
  // (would trigger DataContext syncLots → full POS catalog remount).
  if (options.source !== 'checkout' && options.publishInventoryEvent !== false) {
    publishLegacyWindowEvent('inventoryUpdated', {
      source: options.source || 'lotsRefresh',
      forceRefresh: true,
      timestamp: Date.now()
    });
  }
}

/**
 * True nur bei Greenfield: Achats vorhanden, aber noch kein Lot-System.
 * Operative Inventare (Lots existieren) → nie naggen; Qty-Drift ist Repair, nicht Migration.
 * Robust gegen Array vs. Objekt und flache vs. gruppierte Purchase-Formate.
 * @param {object[]|object|null|undefined} purchases
 * @param {object[]|object|null|undefined} lots
 * @returns {boolean}
 */
export function needsLotMigration(purchases, lots) {
  const purchaseLines = flattenPurchaseLineItems(purchases);
  if (purchaseLines.length === 0) return false;
  const lotsArr = normalizeLotsField(lots);

  // Greenfield: purchases without any lots
  if (lotsArr.length === 0) return true;

  // Operational lot inventory already present → migration UI must stay quiet.
  // (Restock, sales, missing lotId, duplicate CON shells, etc. are NOT migration.)
  if (lotsArr.length >= 10) return false;

  const productsWithLots = new Set(
    lotsArr.map((l) => String(l?.productId)).filter((id) => id && id !== 'null' && id !== 'undefined')
  );
  if (productsWithLots.size === 0) return true;

  const purchaseProducts = new Set(
    purchaseLines
      .map((l) => String(l?.productId))
      .filter((id) => id && id !== 'null' && id !== 'undefined')
  );
  if (purchaseProducts.size === 0) return false;

  let productsCovered = 0;
  for (const pid of purchaseProducts) {
    if (productsWithLots.has(pid)) productsCovered += 1;
  }
  const productCoverRatio = productsCovered / purchaseProducts.size;
  // Sparse leftover lots (<10) only count as "already migrated" if they cover a real share
  if (productCoverRatio >= 0.25) return false;

  // Cover by lotId / purchaseId|productId / lastPurchaseId (restock)
  const lotIds = new Set(lotsArr.map((l) => String(l?.id)).filter(Boolean));
  const lotsByReceiptProduct = new Set();
  for (const lot of lotsArr) {
    if (lot?.productId == null) continue;
    if (lot?.purchaseId != null) {
      lotsByReceiptProduct.add(`${String(lot.purchaseId)}|${String(lot.productId)}`);
    }
    if (lot?.lastPurchaseId != null) {
      lotsByReceiptProduct.add(`${String(lot.lastPurchaseId)}|${String(lot.productId)}`);
    }
  }

  let covered = 0;
  for (const line of purchaseLines) {
    if (line?.lotId != null && line.lotId !== '' && lotIds.has(String(line.lotId))) {
      covered += 1;
      continue;
    }
    if (
      line?.purchaseId != null &&
      line?.productId != null &&
      lotsByReceiptProduct.has(`${String(line.purchaseId)}|${String(line.productId)}`)
    ) {
      covered += 1;
      continue;
    }
    if (line?.productId != null && productsWithLots.has(String(line.productId))) {
      covered += 1;
    }
  }
  if (covered / purchaseLines.length >= 0.5) return false;

  const purchaseQty = purchaseLines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  const anyTotalQty = lotsArr.some((l) => Number.isFinite(Number(l?.totalQuantity)));
  const lotQty = lotsArr.reduce((s, l) => {
    if (anyTotalQty) {
      const total = Number(l?.totalQuantity);
      return s + (Number.isFinite(total) ? total : 0);
    }
    return s + (Number(l?.remainingQuantity) || 0);
  }, 0);
  const qtyDelta = Math.abs(purchaseQty - lotQty);
  if (purchaseQty > 0 && qtyDelta / purchaseQty < 0.05) return false;
  if (qtyDelta <= 0.0001) return false;

  return true;
}

/**
 * Migration von Einkäufen → Lots (entity_* / NO_KV_BLOB-kompatibel).
 * SSOT: 1 lot pro Achat-Zeile (facture/achat wirksam), draft ignoriert.
 *
 * ADR-0016: `force: true` alone no longer rebuilds from achat lines when sale
 * movements exist (would re-inflate remaining). Prefer reproject.
 * Dangerous full rebuild requires:
 *   `{ forceAchatRebuild: true, confirmStockInflation: true }`
 *
 * @param {{
 *   businessId?: string,
 *   purchases?: object[],
 *   force?: boolean,
 *   forceAchatRebuild?: boolean,
 *   confirmStockInflation?: boolean
 * }} [context]
 */
export async function migrateToLotSystemAsync(context = {}) {
  try {
    const businessId = normalizeBusinessStorageId(
      context.businessId ||
        getCurrentBusinessId({ allowFallback: false }) ||
        businessIdFromUser(parseKvCurrentUser())
    );
    if (!businessId) return { success: false, error: 'No business ID found' };

    // Always re-read purchases from entity store — UI context may be grouped/stale (2 factures).
    const { readPurchasesFromBestSource } = await import('./purchasesReadSource');
    const { readLotsFromBestSource } = await import('./lotsReadSource');
    const purchases = await readPurchasesFromBestSource(businessId);
    const existingLots = await readLotsFromBestSource(businessId);

    if (!purchases.length) {
      return { success: false, error: 'No purchases found to migrate' };
    }

    const needs = needsLotMigration(purchases, existingLots);
    if (!context.force && !context.forceAchatRebuild && !needs) {
      return {
        success: true,
        message: 'Lots already aligned with achats',
        lotsCreated: 0
      };
    }

    // Sale history on ledger → never silent achat rebuild
    const parsed = readBestBusinessMergedPayload(businessId)?.parsed || {};
    const moveLists = [
      Array.isArray(parsed.stockMovements) ? parsed.stockMovements : [],
      Array.isArray(parsed.stockMovementsArchive) ? parsed.stockMovementsArchive : []
    ];
    let hasSaleMoves = false;
    for (const list of moveLists) {
      for (const m of list) {
        const t = String(m?.movementType || '').toUpperCase();
        if (t === 'SALE' || t === 'SALE_RETURN' || t === 'CONSUMPTION') {
          hasSaleMoves = true;
          break;
        }
      }
      if (hasSaleMoves) break;
    }

    const allowAchatRebuild =
      context.forceAchatRebuild === true &&
      (context.confirmStockInflation === true || !hasSaleMoves);

    // force without achat rebuild, or blocked rebuild → reproject from ledger
    if (context.force && !allowAchatRebuild) {
      console.warn(
        '[migrateToLotSystemAsync] ADR-0016: reproject instead of achat rebuild' +
          (hasSaleMoves ? ' (sale history present)' : '')
      );
      try {
        const { reprojectLotsFromLedgerForBusiness } = await import(
          '../domain/inventory/recovery'
        );
        const repro = await reprojectLotsFromLedgerForBusiness({
          businessId,
          strict: false
        });
        return {
          success: Boolean(repro?.success),
          reprojected: true,
          lotsCreated: 0,
          error: repro?.error,
          message:
            repro?.success
              ? 'Lots reprojected from inventory ledger (achat force rebuild blocked)'
              : repro?.error || 'reproject failed',
          ...repro
        };
      } catch (err) {
        return {
          success: false,
          error: err?.message || String(err),
          reprojected: false
        };
      }
    }

    if (context.forceAchatRebuild === true && hasSaleMoves && !context.confirmStockInflation) {
      return {
        success: false,
        error: 'force_achat_rebuild_blocked_sale_history',
        message:
          'Achat→lots rebuild blocked after sales. Use reprojectLotsFromLedger or pass confirmStockInflation: true (ops only).'
      };
    }

    // Natural empty migration OR explicitly allowed achat rebuild
    if (!needs && !allowAchatRebuild) {
      return {
        success: true,
        message: 'Lots already aligned with achats',
        lotsCreated: 0
      };
    }

    const { rebuildLotsFromCompletedPurchases } = await import(
      '../domain/inventory/rebuildLotsFromPurchases'
    );
    const rebuilt = await rebuildLotsFromCompletedPurchases({
      businessId,
      purchases,
      clearDraft: true,
      syncInvoiceLotIds: true
    });

    if (!rebuilt?.success) {
      return {
        success: false,
        error: rebuilt?.error || 'rebuildLotsFromCompletedPurchases failed'
      };
    }

    return {
      success: true,
      lotsCreated: Number(rebuilt.lotsCreated || 0),
      purchaseLines: Number(rebuilt.purchaseLines || 0),
      linkedLotIds: Number(rebuilt.linkedLotIds || 0),
      message:
        rebuilt.message ||
        `Lots reconstruites depuis ${rebuilt.purchaseLines || rebuilt.lotsCreated} ligne(s) d'achat`
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Legacy migration function — delegates to async entity-store path.
 * @param {{ businessId?: string, purchases?: object[], force?: boolean }} [context]
 */
export const migrateToLotSystem = (context = {}) => migrateToLotSystemAsync(context);

/**
 * ZENTRALE WERT-BERECHNUNGSFUNKTIONEN
 * Diese Funktionen ersetzen alle manuellen product.value Berechnungen
 */

/**
 * Berechnet den Gesamtwert eines Produkts basierend auf Lots
 * MIGRIERT: Verwendet CalculationFactory für zentrale Berechnung
 * @param {number} productId - Produkt-ID
 * @param {string|null} location - Standort (null = alle Standorte)
 * @returns {number} Gesamtwert des Produkts
 */
export const getProductValue = (productId, location = null) => {
  try {
    const lots = getLotsForProduct(productId, location);
    try {
      const { projectStockFromLots } = require('../domain/inventory/stock');
      return projectStockFromLots(productId, location, lots).value;
    } catch (factoryError) {
      const sellableLots = lots.filter((lot) => isLotAvailableForSale(lot));
      return sellableLots.reduce((total, lot) => {
        return total + lot.remainingQuantity * lot.purchaseUnitPrice;
      }, 0);
    }
  } catch (error) {
    return 0;
  }
};

/**
 * Berechnet die Gesamtmenge eines Produkts basierend auf Lots.
 * Prefer getSellableQty from inventoryAvailability at call sites;
 * this remains a sync alias with the same lot-inclusion rule.
 * @param {string|number} productId
 * @param {string|null} location - Standort (null = alle Standorte)
 * @returns {number}
 */
export const getProductQuantity = (productId, location = null) => {
  try {
    if (location != null && location !== '') {
      // Sync require avoids static cycle with inventoryAvailability → lotSystem
      // eslint-disable-next-line global-require
      const { getSellableQty } = require('./inventoryAvailability');
      return getSellableQty(productId, location, {
        lots: getLotsForProduct(productId, location)
      });
    }
    const lots = getLotsForProduct(productId, null);
    return lots
      .filter((lot) => isLotAvailableForSale(lot))
      .reduce((total, lot) => total + Number(lot.remainingQuantity || 0), 0);
  } catch (error) {
    return 0;
  }
};

/**
 * Cached sellable qty — same semantics as getProductQuantity / getSellableQty.
 * Guards legacy wrong arity: (productId, lotsArray, location).
 */
export const getProductQuantityOptimized = (productId, locationOrLots = null, maybeLocation) => {
  const location = Array.isArray(locationOrLots)
    ? maybeLocation ?? 'Principal'
    : locationOrLots;
  const cacheKey = getCacheKey(productId, location, 'getProductQuantity');

  if (calculationCache.has(cacheKey)) {
    return calculationCache.get(cacheKey);
  }

  const quantity = getProductQuantity(productId, location);
  calculationCache.set(cacheKey, quantity);
  return quantity;
};

/**
 * Berechnet den durchschnittlichen Verkaufspreis eines Produkts basierend auf Lots
 * @param {number} productId - Produkt-ID
 * @param {string|null} location - Standort (null = alle Standorte)
 * @returns {number} Durchschnittlicher Verkaufspreis
 */
export const getProductSellingPrice = (productId, location = null) => {
  try {
    const lots = getLotsForProduct(productId, location);
    const sellableLots = lots.filter((lot) => isLotAvailableForSale(lot));
    
    if (sellableLots.length === 0) return 0;
    
    const totalQuantity = sellableLots.reduce((sum, lot) => sum + lot.remainingQuantity, 0);
    const totalValue = sellableLots.reduce((sum, lot) => sum + (lot.remainingQuantity * lot.sellingUnitPrice), 0);
    
    return totalValue / totalQuantity;
  } catch (error) {
    return 0;
  }
};

export const getProductPotentialProfit = (productId, location = null) => {
  try {
    const lots = getLotsForProduct(productId, location);
    const sellableLots = lots.filter((lot) => isLotAvailableForSale(lot));
    if (sellableLots.length === 0) return 0;
    const totalPotentialProfit = sellableLots.reduce((sum, lot) => {
      return sum + (lot.remainingQuantity * (lot.sellingUnitPrice - lot.purchaseUnitPrice));
    }, 0);
    return totalPotentialProfit;
  } catch (error) {
    return 0;
  }
};

/**
 * Aktualisiert den Verkaufspreis aller Lots eines Produkts
 * @param {number} productId - Produkt-ID
 * @param {number} newSellingPrice - Neuer Verkaufspreis
 * @param {string|null} location - Standort (null = alle Standorte)
 * @returns {boolean} Erfolg der Aktualisierung
 */
export const updateProductSellingPrice = (productId, newSellingPrice, location = null) => {
  try {
    const user = parseKvCurrentUser();
    if (!user) return false;

    const businessId = businessIdFromUser(user);

    if (!businessId) return false;

    let updated = false;
    const locationsToRefresh = new Set();

    const wr = persistBusinessPayloadSync(businessId, (parsed) => {
      const lots = parsed.lots || [];
      lots.forEach((lot) => {
        if (sameProductId(lot.productId, productId) && (location === null || lot.location === location)) {
          lot.sellingUnitPrice = Number(newSellingPrice);
          lot.totalSellingValue = lot.remainingQuantity * lot.sellingUnitPrice;
          lot.potentialProfit = lot.totalSellingValue - lot.totalPurchaseValue;
          lot.updatedAt = new Date().toISOString();
          updated = true;
          locationsToRefresh.add(lot.location);
        }
      });
      return parsed;
    });

    if (updated && wr.success) {
      if (location) {
        updateStockFromLots(productId, location);
      } else {
        locationsToRefresh.forEach((loc) => updateStockFromLots(productId, loc));
      }
      return true;
    }

    return false;
  } catch (error) {
    return false;
  }
};

/**
 * Findet ähnliche Lots basierend auf gemeinsamen Attributen
 * Wird für Transfer-Optimierung verwendet
 * 
 * @param {string} productId - ID des Produkts
 * @param {string} location - Zielstandort
 * @param {Object} sourceLot - Quell-Lot zum Vergleich
 * @returns {Object|null} Ähnliches Lot oder null
 */
export const findSimilarLot = (productId, location, sourceLot) => {
  try {
    const lots = getLotsForProduct(productId, location);
    return lots.find((existingLot) => lotsAreSameLogicalLot(existingLot, sourceLot)) || null;
  } catch (error) {
    return null;
  }
};

/**
 * Konsolidiert ähnliche Lots in einem Standort (Transfer-Optimierung).
 * ADR-0016: remaining deltas via ADJUSTMENT projection (no dual-write).
 *
 * @param {string} productId
 * @param {string} location
 * @returns {boolean}
 */
export const consolidateSimilarLots = (productId, location) => {
  try {
    const lots = getLotsForProduct(productId, location);
    if (lots.length <= 1) return false;

    const consolidatedLots = [];
    const processedLots = new Set();
    const locationKey = normalizeLotLocationKey(location);

    for (let i = 0; i < lots.length; i++) {
      if (processedLots.has(i)) continue;

      const currentLot = lots[i];
      const similarLots = [currentLot];
      processedLots.add(i);

      for (let j = i + 1; j < lots.length; j++) {
        if (processedLots.has(j)) continue;
        const otherLot = lots[j];
        if (lotsAreSameLogicalLot(otherLot, currentLot)) {
          similarLots.push(otherLot);
          processedLots.add(j);
        }
      }

      if (similarLots.length > 1) {
        const consolidatedLot = recomputeLotValues({
          ...currentLot,
          totalQuantity: similarLots.reduce(
            (sum, lot) => sum + (Number(lot.totalQuantity) || 0),
            0
          ),
          remainingQuantity: similarLots.reduce(
            (sum, lot) => sum + (Number(lot.remainingQuantity) || 0),
            0
          ),
          updatedAt: new Date().toISOString()
        });
        consolidatedLots.push(consolidatedLot);
      } else {
        consolidatedLots.push(currentLot);
      }
    }

    const bid = getCurrentBusinessId();
    if (!bid) return false;

    const beforeCount = lots.length;
    if (consolidatedLots.length === beforeCount) {
      // Only id-ghosts possible; still run ledger plan for dedupe residual
    }

    const wr = persistBusinessPayloadSync(bid, (parsed) => {
      ensureLotsArray(parsed);
      const liveAll = Array.isArray(parsed.lots) ? [...parsed.lots] : [];
      const otherLots = liveAll.filter(
        (lot) =>
          !(
            sameProductId(lot.productId, productId) &&
            normalizeLotLocationKey(lot.location) === locationKey
          )
      );
      const afterLots = dedupeLotsList([...otherLots, ...consolidatedLots]);
      applyPlannedLotsViaLedger(parsed, liveAll, afterLots, 'lots_consolidate_similar', {
        businessId: bid,
        movementTypePolicy: 'adjustment'
      });
      return parsed;
    });

    if (!wr.success) return false;

    updateStockFromLots(productId, location);
    return consolidatedLots.length !== beforeCount
      ? true
      : beforeCount !== dedupeLotsList(lots).length;
  } catch (error) {
    console.error('[consolidateSimilarLots]', error?.message || error);
    return false;
  }
};
